import { Component, OnDestroy, OnInit } from '@angular/core';
import { DataService } from '../../../../../services/data/data.service';
import { filter, Observable, Subject, takeUntil } from 'rxjs';
import { MENU_LABELS, navMenu, PAYMENT_FLOW_PAST_DUE, UserMenu } from '../../../../../utils/constants';
import { AuthDataService } from '../../../../../services/auth/auth-data.service';
import { MenuItem } from 'primeng/api';
import { RouterService } from '../../../../../services/router/router.service';
import { APP_ROUTES } from '../../../../../utils/route.constant';
import { IPaymentBannerItem } from '../../../../../interfaces/IBanner';
import { IContractData } from '../../../../../interfaces/IJob';
import { UtilitiesService } from '../../../../../services/utilities/utilities.service';
import { CONTRACT_STATUS_UPPER_INACTIVE, CONTRACT_STATUS_UPPER_CANCELED, CONTRACT_STATUS_UPPER_EXPIRED } from '../../../../pages/my-plan/my-plan.constants';
import { NavigationEnd, Router } from '@angular/router';
import { Ga4Service } from '../../../../../services/ga4/ga4.service';
import { DASHBOARD_PAGE_LOCATION, LOG_OUT, PAY_PAST_DUE, RED_BANNER } from '../../../../../services/ga4/ga4.constant';
import { IFeatureFlag } from '../../../../../interfaces/IFeatureFlags';
import { FEATURES_CONSTANT } from '../../../../../utils/features.constant';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit, OnDestroy {
  user$: Observable<any> | undefined;
  logoClass$: Observable<string | null> | undefined;
  visible: boolean = false;
  userMenu: any;
  items: MenuItem[] = [];
  cleaner$ = new Subject<void>();
  paymentBanner: IPaymentBannerItem | null = null;
  navItems: MenuItem[] | undefined = navMenu;
  isRequestServiceEnable: boolean = true;
  isPlanFFS:boolean = false;
  showFfsBanner:boolean = false;
  hideNavigation$: Observable<boolean> | undefined;

  constructor(
    private dataService: DataService,
    private ga4Service: Ga4Service,
    private authData: AuthDataService,
    private routerService: RouterService,
    private utilService: UtilitiesService,
    private router: Router
  ) { }


  ngOnInit(): void {
    this.user$ = this.dataService.user$;
    this.hideNavigation$ = this.dataService.hideNavigation$;

    this.showFfsBanner = this.computeShowFfsBanner(this.router.url);
    this.router.events
    .pipe(
      takeUntil(this.cleaner$),
      filter((e): e is NavigationEnd => e instanceof NavigationEnd)
    )
    .subscribe(e => {
      this.showFfsBanner = this.computeShowFfsBanner(e.urlAfterRedirects ?? e.url);
    });
    this.logoClass$ = this.dataService.LogoClass$;
    this.initializeUserMenu();
    if(this.user$){
      this.user$.pipe(takeUntil(this.cleaner$)).subscribe({
        next: (user) => {
          if (user) {
            this.userMenu[0].label = user['name'];
            this.getContractDetails();
          }
        },
      });
    } else{
      console.error('user$ is undefined');
    }

  }

  navigateToPayment(): void {
    this.dataService.paymentInitiation$.next(PAYMENT_FLOW_PAST_DUE);
    this.ga4Service.buttonClickEvent(RED_BANNER, PAY_PAST_DUE);
    this.routerService.goto(APP_ROUTES.MAKE_PAYMENT);

  }

  private getBaseUserMenuItems(hide: boolean) : MenuItem[] {
    if (hide) {
      return [];
    }
    return structuredClone(UserMenu[0].items);
  }

  private initializeUserMenu(): void {
    this.hideNavigation$?.pipe(takeUntil(this.cleaner$)).subscribe(hide => {
      const items = this.getBaseUserMenuItems(hide);
      
      items.push({
        label: 'Log Out',
        icon: 'pi pi-sign-out',
        command: () => {
          this.ga4Service.buttonClickEvent(DASHBOARD_PAGE_LOCATION, LOG_OUT);
          this.authData.logout();
        }
      });

      const currentLabel = this.userMenu ? this.userMenu[0].label : UserMenu[0].label;
      this.userMenu = [{
        label: currentLabel,
        items: items
      }];
    });
  }

  getContractDetails(): void {
    this.dataService.contractData$.pipe(takeUntil(this.cleaner$)).subscribe({
      next: (contract: IContractData | null) => {
        if (contract) {
          this.paymentBanner = this.dataService.getPaymentBannerItem();
          this.filterMenuItems(contract);
          this.isPlanFFS = this.dataService.isPlanFFS();
          this.showFfsBanner = this.computeShowFfsBanner(this.router.url);
        }
      },
    });
  }


  /**
   * @description This method is used to filter the menu items based on the contract status and user permissions
   * @param contract 
   * @returns 
   */
  filterMenuItems(contract: IContractData): void {
    if (!contract || !contract.baseContract || !contract.baseContract.contractStatus) {
      return;
    }

    const contractStatus = contract.baseContract.contractStatus.toLowerCase();
    const isPlanStatusIncluded = [
      CONTRACT_STATUS_UPPER_EXPIRED.toLowerCase(),
      CONTRACT_STATUS_UPPER_INACTIVE.toLowerCase(),
      CONTRACT_STATUS_UPPER_CANCELED.toLowerCase()
    ].includes(contractStatus);
    const isFinancialChannel = contract.userStatuses?.IS_FINANCIAL_CHANNEL;
    const isPlanEMPLYD = this.utilService.isPlanEMPLYD(contract.baseContract.clientId);
    const isBillOnceChange = contract.userStatuses?.BILL_ONCE_CHANGE;
    const isPlanCE = this.utilService.isPlanCE(contract.baseContract.coverageGroup);
    const isPlanMakeCreditCard = this.utilService.isPlanBillMechCodeEmbed(contract.baseContract.billMechCode);
    const canPlaceClaim = contract.userPermissions?.PLACE_MY_CLAIM;
    const featureFlags = this.dataService.featureFlags$.getValue();
    const moenAndH4hFeatureFlag = featureFlags?.features?.find((feature: IFeatureFlag) => feature.feature === FEATURES_CONSTANT.MOENANDH4H)?.flag || false;
    const isPlanBillMechCodeBillClient = moenAndH4hFeatureFlag && this.utilService.isPlanBillMechCodeBillClient(contract.baseContract.billMechCode);

    const removeNavItems = (labelsToRemove: string[]) => {
      this.navItems = structuredClone(navMenu)
                        .flatMap(navItem => navItem.items || [])
                        .filter(item => !labelsToRemove.includes(item.label ?? ''));
    };

    const itemsToRemove = [];

    ((isFinancialChannel && !isPlanEMPLYD && !isBillOnceChange) || isPlanMakeCreditCard ||  isPlanBillMechCodeBillClient) && itemsToRemove.push(MENU_LABELS.payments);
    ((canPlaceClaim && (contractStatus === CONTRACT_STATUS_UPPER_INACTIVE.toLowerCase())) || isPlanCE) && itemsToRemove.push(MENU_LABELS.myServiceRequests);
    !this.utilService.isPerksBenefitsVisible(contract) && itemsToRemove.push(MENU_LABELS.perksBenefits);
    this.isRequestServiceEnable = !isPlanStatusIncluded && canPlaceClaim && !isPlanCE;

    removeNavItems(itemsToRemove);
  }

  private computeShowFfsBanner(url: string): boolean {
    const base = url.split('?')[0].replace(/\/+$/, '');
    return this.dataService.isPlanFFS() && base !== '/' + APP_ROUTES.SERVICE_REQUEST_CONFIRMATION;
  }

  ngOnDestroy(): void {
    this.cleaner$.next();
    this.cleaner$.complete();
  }
}
